import * as React from "react";
import {FC, useState} from "react";
import {useParams} from "react-router-dom";
import Page from "../../../layout/Page";
import PageHeader from "../../../layout/PageHeader";
import PageContent from "../../../layout/PageContent";
import {
   useVoucherPageAdjustmentMutation,
   useVoucherPageSendReceivedMailMutation,
   useVoucherPageSynchronizeMutation,
   useVoucherPageVoucherQuery,
   VoucherPageVoucherDocument,
   VoucherUsagesDocument,
   VoucherUsagesGiftCardSyncDocument
} from "../../../Queries";
import {Alert, Box, Button, Dialog, DialogContent, LinearProgress} from "@mui/material";
import PageGroup from "../../../layout/PageGroup";
import {VoucherPanel} from "./VoucherPanel";
import {SavingUI} from "../../../SavingProvider";
import {VoucherUsages} from "./VoucherUsages";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../../atoms/CurrentUser";
import {FormProvider, useForm} from "react-hook-form";
import {TitleText} from "../../../layout/Typography";
import {ConsumerPanel} from "../../shop/components/ConsumerPanel";
import {FormInputPrice} from "../../shop/wholesale/form/FormInputPrice";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {ObjectHistory} from "../../components/ObjectHistory";

export const VoucherPagePath = (brandHouseId: string, shopId: string, voucherId: string) => {
   return "/brands/" + brandHouseId + "/rewards/" + voucherId;
}

export const VoucherPage: FC<any> = () => {
   const {voucherId} = useParams<{ brandHouseId: string, voucherId: string }>();
   const [adjust, setAdjust] = useState<boolean>(false)
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);

   const {data} = useVoucherPageVoucherQuery({
      variables: {
         id: voucherId
      }
   });
   const [syncVoucher] = useVoucherPageSynchronizeMutation();
   const [sendReceivedMail] = useVoucherPageSendReceivedMailMutation()

   if (!data?.VoucherById) {
      return <LinearProgress/>
   }
   const voucher = data!.VoucherById
   const shopId = voucher!.shopId!

   const onSendReceived = async () => {
      await SavingUI.process(async () => {
         await sendReceivedMail({
            variables: {
               shopId: shopId,
               options: {
                  received: {
                     voucherId: voucherId
                  }
               }
            }
         });
      }, 'Sent received mail')
   }

   const onSync = async () => {
      await SavingUI.process(async () => {
         await syncVoucher({
            variables: {
               shopId: shopId,
               code: data!.VoucherById!.code!
            }
         });
      }, 'Synchronized')
   }

   const onAdjust = () => {
      setAdjust(true)
   }

   return <Page>
      <PageHeader title="Reward details" onBack={true}>
         <Button onClick={onSendReceived}>Send received mail</Button>
      </PageHeader>
      <PageGroup widths={['75%', '25%']}>
         <PageContent fullHeight commands={<>
            <Button onClick={onAdjust}>Adjust voucher</Button>
            {(!voucher.passive || isAdministrator) && <Button onClick={onSync}>Synchronize</Button>}
         </>}>
            <VoucherPanel voucherId={voucherId}/>
            <div style={{marginTop: '20px'}}>
               {!voucher?.newVoucherUsage &&
                  <Alert severity={"info"}>This voucher has not been converted to new voucher system</Alert>
               }
               <VoucherUsages voucherId={voucherId}/>
            </div>
         </PageContent>
         <PageContent fullHeight>
            <ConsumerPanel consumerId={voucher.consumerId}/>
         </PageContent>
      </PageGroup>
      <ObjectHistory refId={voucherId}/>
      <Dialog open={adjust} maxWidth={"md"} onClose={() => setAdjust(false)} fullWidth>
         <DialogContent>
            <AdjustForm shopId={shopId} passive={!!voucher.passive} onHide={() => setAdjust(false)}/>
         </DialogContent>
      </Dialog>
   </Page>
}

type AdjustFormData = {
   voucherId: string
   shopId: string
   amount: number
   reason: string
}

type AdjustFormProps = {
   shopId: string
   /** The voucher's balance is held by its gift card in the shop's system, so the adjustment is applied there too */
   passive: boolean
   onHide: () => void
}

/**
 * Adds or subtracts an amount on the voucher. On a passive voucher the adjustment is created with applyToGiftCard,
 * so the shop's integration applies it to the gift card as well (C2S-1324); the timeline shows whether it did.
 */
const AdjustForm = ({shopId, passive, onHide}: AdjustFormProps) => {
   const {voucherId} = useParams<{ voucherId: string }>();
   const form = useForm<AdjustFormData>({
      defaultValues: {
         amount: 0,
         reason: ''
      }
   });
   const {handleSubmit} = form;
   const [adjust] = useVoucherPageAdjustmentMutation()

   const onSave = async (data: AdjustFormData) => {
      if (!data.amount || !data.reason) {
         return;
      }
      await SavingUI.process(async () => {
         await adjust({
            variables: {
               adjustment: {
                  amount: data.amount,
                  shopId: shopId,
                  voucherId: voucherId,
                  reason: data.reason,
                  date: new Date(),
                  applyToGiftCard: passive
               }
            },
            refetchQueries: [{
               query: VoucherUsagesDocument,
               variables: {
                  voucherId: voucherId
               }
            }, {
               query: VoucherUsagesGiftCardSyncDocument,
               variables: {
                  shopId: shopId,
                  voucherId: voucherId
               }
            }, {
               query: VoucherPageVoucherDocument,
               variables: {
                  id: voucherId
               }
            }]
         })
      }, 'Voucher updated', 'Please wait')
      onHide()
   }

   return <FormProvider {...form}>
      <Box>
         <TitleText type={"h1"}>Adjust the voucher value</TitleText>
         <TitleText type={"subtitle1"}>You can add or subtract an amount of the voucher here. If you need to correct the
            adjustment afterwards, do this by making another adjustment</TitleText>
         {passive && <Alert severity={"info"} sx={{marginBottom: '16px'}}>
             This voucher is a gift card in the shop, so the adjustment is also made on the gift card. Subtracting more
             than the gift card's balance fails. The timeline shows whether it was applied.
         </Alert>}

         <FormInputPrice name={"amount"} label={"Amount"} allowNegative={true}/>
         <FormInputText name={"reason"} label={"Reason for adjustment"}/>

         <Box sx={{display: 'flex', gap: '10px'}}>
            <Button variant={"contained"} onClick={handleSubmit(onSave)}>Adjust</Button>
            <Button variant={"outlined"} onClick={onHide}>Cancel</Button>
         </Box>
      </Box>
   </FormProvider>
}